import { AnnotationMode, RenderingCancelledException } from 'pdfjs-dist';
import { PDFPageProxy } from 'pdfjs-dist/types/src/pdf';
import {
  EventBus,
  PDFPageView,
  RenderingStates,
} from 'pdfjs-dist/web/pdf_viewer';
import React, { useEffect, useRef, useState } from 'react';
import '../../scss/pdfViewer.scss';

import { LoadingIcon } from '@readcloud/component-library';
import { readerSelectors } from '@readcloud/state';
import { useSelector } from 'react-redux';
import { DocumentWithExtrasContext } from '../../context/DocumentWithExtras';
import { TextSelectionContext } from '../../context/TextSelection';
import { ConnectedPDFHighlightsRenderer } from '../AnnotationLayers/ConnectedPDFHighlightsRenderer';
import { ConnectedShapesRenderer } from '../AnnotationLayers/ConnectedShapesRenderer';
import { SelectionStream } from '../CustomSelection/SelectionStream';
import { ConnectedLearningToolbar } from '../Toolbars/ConnectedLearningToolbar';

interface IPDFPageProps {
  page?: PDFPageProxy;
  scale?: number;
  height: number;
  width: number;
  pdfViewerContainer: HTMLDivElement;
  pdfGridContainer: HTMLDivElement;
  pdfGridCell: HTMLDivElement;
}

const PDFPage: React.FunctionComponent<IPDFPageProps> = ({
  page,
  scale = 1.0,
  height,
  width,
  pdfViewerContainer,
  pdfGridContainer,
  pdfGridCell,
}) => {
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const pageViewRef = useRef<PDFPageView>();
  const [pageView, setPageView] = useState<PDFPageView>();
  const [pageInitialized, setPageInitialized] = useState<boolean>(false);
  const drawTimeoutId = useRef<number>(null);
  const readerData = useSelector(readerSelectors.getReaderData);

  const { extras } = React.useContext(DocumentWithExtrasContext);
  const { setSelectionState, customTextSelection } =
    React.useContext(TextSelectionContext);

  const [rendering, setRendering] = React.useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    if (!page || pageInitialized) {
      return () => {
        cancelled = true;
      };
    }

    setPageInitialized(true);
    //get the page from the pdf and draw the pageview to the container.

    const viewport = page.getViewport({ scale: scale });

    document.documentElement.style.setProperty(
      '--scale-factor',
      `${viewport.scale}`
    );

    const tempPageView = new PDFPageView({
      container: pdfContainerRef.current || undefined,
      eventBus: new EventBus(),
      id: page.pageNumber,
      defaultViewport: viewport.clone(),
      isOffscreenCanvasSupported: true,
      scale: scale,
      annotationMode: AnnotationMode.ENABLE,
      layerProperties: () => ({
        get findController() {
          return extras.pdfFindController;
        },
        get linkService() {
          return extras.linkService;
        },
      }),
    });

    tempPageView.div.style.setProperty(
      'width',
      `calc(var(--scale-factor) * ${viewport.rawDims['pageWidth']})`
    );
    tempPageView.div.style.setProperty(
      'height',
      `calc(var(--scale-factor) * ${viewport.rawDims['pageHeight']})`
    );

    tempPageView?.setPdfPage(page);

    tempPageView.draw().then(() => {
      if (cancelled) return;
      setPageView(tempPageView);
    });
    return () => {
      cancelled = true;
      tempPageView.reset();
    };
  }, [page]);

  useEffect(() => {
    return () => {
      pageView && pageView.destroy();
    };
  }, []);

  useEffect(() => {
    if (pageView?.textLayer) {
      // we want to rerender the textlayer every time the search match changes.
      // textlayer.show() seems to be good enough to get react to rerender with the changes pdfjs made.
      // no idea why.
      pageView?.update({});
      pageView?.textLayer?.show();
    }
  }, [
    extras.pdfFindController?.selected?.pageIdx,
    extras.pdfFindController?.selected?.matchIdx,
    pageView?.id,
  ]);

  const onContextMenu = React.useCallback(
    (e: MouseEvent) => {
      if (customTextSelection) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [customTextSelection]
  );

  React.useEffect(() => {
    if (pageView?.textLayer?.div) {
      pageView?.textLayer?.div?.addEventListener('contextmenu', onContextMenu);
    }

    return () => {
      if (pageView?.textLayer?.div) {
        pageView?.textLayer?.div?.removeEventListener(
          'contextmenu',
          onContextMenu
        );
      }
    };
  }, [!!pageView?.textLayer?.div]);

  useEffect(() => {
    if (pageView && pageInitialized) {
      //update page after 400ms with new scale (this temporarily updates css.)
      document.documentElement.style.setProperty('--scale-factor', `${scale}`);

      pageView?.update({
        scale: scale,
        drawingDelay: 400,
      });

      //clear the timeout if.
      if (drawTimeoutId.current !== null) {
        clearTimeout(drawTimeoutId.current);
        drawTimeoutId.current = null;
      }
      //then once the delay has happened, we want to redraw
      drawTimeoutId.current = window.setTimeout(() => {
        drawTimeoutId.current = null;
        //for some reason we need to call update again with no args
        pageView?.update({});
        pageView.draw().catch((reason) => {
          if (reason instanceof RenderingCancelledException) {
            return;
          }
          console.error(`renderView: "${reason}"`);
        });
      }, 400);
    }
  }, [pageView, scale]);

  React.useEffect(() => {
    if (pageView?.renderingState) {
      if (pageView?.renderingState === RenderingStates.FINISHED) {
        setRendering(false);
      }
    }
  }, [pageView?.renderingState]);

  return (
    <>
      {page && (
        <>
          <div
            ref={pdfContainerRef}
            id="pdfContainer"
            className={
              customTextSelection ? 'iosTextSelectNone textSelectNone' : ''
            }
          />
          {pageView?.textLayer?.div && pdfContainerRef.current && (
            <>
              <SelectionStream
                onSelectionStateChange={setSelectionState}
                custom={customTextSelection}
                element={pageView?.textLayer?.div}
                boundsElement={pageView?.textLayer?.div}
                markersContainer={pdfContainerRef.current}
                selectionCanvasContainer={pdfContainerRef.current}
                location={page.pageNumber.toString()}
              ></SelectionStream>
            </>
          )}
          <ConnectedShapesRenderer
            pageNumber={page.pageNumber}
            height={height}
            width={width}
            scale={scale}
            outerContainer={pdfViewerContainer}
            pagesContainer={pdfGridContainer}
            pageContainer={pdfGridCell}
          />

          <ConnectedPDFHighlightsRenderer
            scale={scale}
            pageNumber={page.pageNumber}
            pageContainer={pageView?.div}
            rootContainer={pageView?.textLayer?.div}
            visible
          />
          <ConnectedLearningToolbar
            container={pdfContainerRef.current}
            location={page.pageNumber.toString()}
          />
        </>
      )}
      <div
        className="page-template rc-w-full rc-h-full rc-absolute rc-top-0 rc-left-0 rc-z-[-1]"
        style={{
          width: width,
          height: height,
        }}
      />

      <LoadingIcon loading={rendering} position={'absolute'} />
    </>
  );
};

export default PDFPage;
